import React, { useMemo, useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import useHdyhOptions from '~/acquisition/utils/useHdyhOptions';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import { mobile } from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import { QuizNativeSelect } from '../../components/QuizFormComponents';
import TieredListDropdown from '../TieredListDropdown/TieredListDropdown';

const StyledQuizNativeSelectWrapper = styled.div`
  ${({ theme: { colors } }) => css`
    border: 1px solid ${colors.border};
    border-radius: ${({ theme }) => theme.borderRadiusInput};
  `}

  ${mobile`
    ${({ $isSkippedQuiz }) =>
      !$isSkippedQuiz &&
      css`
        border-radius: 8px;
        border-top-right-radius: 0px;
        border-top-left-radius: 0px;
        min-height: 62px;
      `}
  `}

  ${({ src, theme: { colors } }) =>
    src === 'SignupForm' &&
    `
          border-radius: 0;
          padding: 3px 0 3px 6px;
          background: ${colors.palette.white};
        `}

  ${({ src }) =>
    src === 'SpeedySignupForm' &&
    `
          border: 0;
          padding-left: 8px;
          flex: 1;

          #quiz-native-select label {
            font-size: 14px;
          }
        `}
  ${({ src }) =>
    src === 'SkippedQuizRegistration' &&
    `
          border: 0;
        `}
`;

const StyledQuizNativeSelect = styled(QuizNativeSelect)`
  ${({ src }) =>
    src === 'SkippedRegistration'
      ? `
          ${({ theme }) => theme.quiz.skippedRegSelect}
        `
      : ''}
  border: none;
`;

const HowDidYouHearDropdownWrapper = styled.div`
  ${({ theme }) => theme.quiz.HowDidYouHearDropdownWrapper}

  ${({ $hasError, src, theme }) => {
    if ($hasError && src === 'SpeedySignupForm') {
      return css`
        border: unset;
        border-radius: 0;
        border-bottom: 1px solid ${theme.colors.error};
      `;
    }
    if (
      ($hasError && src === 'BuilderQuizSizingPage') ||
      ($hasError && src === 'SkippedQuizRegistration')
    ) {
      return css`
        border-radius: 4px;
        border: 1px solid ${theme.colors.error} !important;
      `;
    }
    if ($hasError && src === 'SignupForm') {
      return css`
        border-bottom: none !important;
        border: 1px solid ${theme.colors.error};
      `;
    }
    if ($hasError) {
      return css`
        border: 1px solid ${theme.colors.error};
      `;
    }
    return '';
  }}

  ${mobile`
    ${({ $isSkippedQuiz, $hasError }) =>
      $isSkippedQuiz
        ? css`
            min-height: 56px;
          `
        : css`
            border-top-right-radius: 0px;
            border-top-left-radius: 0px;
            ${$hasError &&
            css`
              border-bottom-left-radius: 12px;
              border-bottom-right-radius: 12px;
            `}
          `}
  `}
`;

const NativeHDYHDropdown = ({
  values,
  errors,
  setFieldValue,
  setErrors,
  handleFormUpdate,
  additionalNativeSelectStyles,
  className,
  src,
  $isSkippedQuiz,
  hasError = false,
}) => {
  const intl = useIntl();
  const { tld } = useDomain();
  const isUSDomain = tld === '.com';
  const options = useHdyhOptions();

  const dropdownOptions = useMemo(
    () =>
      options.map(({ referrerId, label }) => ({
        value: referrerId.toString(),
        label,
      })),
    [options]
  );

  return (
    <StyledQuizNativeSelectWrapper src={src}>
      <StyledQuizNativeSelect
        id="hdyh-quiz-dropdown-native-select"
        data-autotag="quiz_how_you_hear_about"
        name="referrer"
        handleFormUpdate={handleFormUpdate}
        additionalStyles={additionalNativeSelectStyles}
        className={className}
        label={intl.formatMessage({
          id: 'quiz.sizing_screen_HDYH_header',
          defaultMessage: 'How did you hear about us?*',
        })}
        errors={errors}
        setErrors={setErrors}
        setFieldValue={setFieldValue}
        showZip={isUSDomain}
        options={dropdownOptions}
        $isSkippedQuiz={$isSkippedQuiz}
        values={values}
        hasError={hasError}
      />
    </StyledQuizNativeSelectWrapper>
  );
};

const HowDidYouHearDropdown = ({
  values,
  errors,
  setFieldValue,
  setErrors,
  handleFormUpdate,
  additionalNativeSelectStyles,
  className,
  src,
  $isSkippedQuiz = false,
  hasError = false,
}) => {
  const isSignup = src === 'SignupForm';
  const { enabled: isTieredHDYHFeatureEnabled } = useHasFeature(
    featureNames.HDYH_TIERED_LIST
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleChange = useCallback(
    (option) => {
      const referrer = option || { value: '', label: '' };
      setFieldValue('referrer', referrer);
      setErrors({ ...errors, referrer: '' });
      /*
      'handleFormUpdate' is responsible for updating additional states or triggering side effects when form values change.
      It doesn't directly update Formik's internal state but relies on setFieldValue to do so.
    */
      handleFormUpdate({
        ...values,
        referrer: option || { value: '', label: '' },
      }); // calls UpdateAnswer
    },
    [setFieldValue, setErrors, errors, handleFormUpdate, values]
  );

  if (isTieredHDYHFeatureEnabled) {
    return (
      <HowDidYouHearDropdownWrapper
        $hasError={hasError && !!errors.referrer}
        src={src}
      >
        <TieredListDropdown
          values={values}
          errors={errors}
          handleChange={handleChange}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          hasError={hasError}
          showRequiredError={isSignup}
        />
      </HowDidYouHearDropdownWrapper>
    );
  }

  return (
    <HowDidYouHearDropdownWrapper
      $isSkippedQuiz={$isSkippedQuiz}
      $hasError={hasError && !!errors.referrer}
      src={src}
    >
      <NativeHDYHDropdown
        values={values}
        errors={errors}
        setFieldValue={setFieldValue}
        setErrors={setErrors}
        handleFormUpdate={handleFormUpdate}
        additionalNativeSelectStyles={additionalNativeSelectStyles}
        className={className}
        src={src}
        $isSkippedQuiz={$isSkippedQuiz}
        hasError={hasError}
      />
    </HowDidYouHearDropdownWrapper>
  );
};

NativeHDYHDropdown.propTypes = {
  $isSkippedQuiz: PropTypes.bool,
  additionalNativeSelectStyles: PropTypes.object,
  className: PropTypes.string,
  errors: PropTypes.object.isRequired,
  handleFormUpdate: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  src: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
};

HowDidYouHearDropdown.propTypes = {
  $isSkippedQuiz: PropTypes.bool,
  additionalNativeSelectStyles: PropTypes.object,
  className: PropTypes.string,
  errors: PropTypes.object.isRequired,
  handleFormUpdate: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  src: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
};

export default HowDidYouHearDropdown;
